import { ModelFilterCategory } from '@store/zustand/model-filters';
import type { CiaFilterCategory } from '@utils/modelFilterCounts';

/**
 * One entry of a category panel's value list: the stored value and the text
 * the row shows for it.
 */
export type CategoryOption = { value: string; label: string };

/**
 * One criteria of a CIA category: its configured name and the answers the
 * values panel lists for it. Assurance, Coverage and Applicability are each
 * a list of these rather than a flat value list.
 */
export type CategoryCriteria = {
  id: string;
  name: string;
  options: CategoryOption[];
};

/**
 * The root category order, grouped exactly as the root popover's dividers
 * group it.
 *
 * Report inclusion closes the fourth group, after Applicability, and is
 * offered only while the subscription includes the Secure-by-Design insight.
 * CIS-18 reference stands alone in the fifth group, last of the root, and is
 * offered only while the project's reference model includes CIS-18.
 * `useCategoryOptions` sets the availability of both.
 */
export const CATEGORY_GROUPS: ModelFilterCategory[][] = [
  ['domains'],
  ['validationStatus', 'reviewStatus', 'operationalStatuses'],
  ['operatingModelStatuses', 'partners'],
  ['assurance_level', 'coverage', 'applicability', 'reportInclusion'],
  ['cis'],
];

/**
 * The default display name of each category. The root row, the chip and the
 * +N row read it through `useCategoryOptions`'s titles, which replace the
 * three CIA entries with the criteria's own name when the category has
 * exactly one (`ciaCategoryTitle`). The panel headers do not go through
 * titles: the criteria list reads this map directly, and the values panel
 * takes the criteria's name from the container when it has one, falling
 * back to this map otherwise. "Assurance" is the several-criteria default of
 * AC2.2.2, and is what the default configuration's three Assurance criteria
 * resolve to; "Assurance level" is a name a tailored configuration may give
 * a single Assurance criteria, and reaches the row through that rule alone.
 */
export const CATEGORY_LABELS: Record<ModelFilterCategory, string> = {
  domains: 'Scope',
  validationStatus: 'Validation status',
  reviewStatus: 'Review status',
  operationalStatuses: 'Operational status',
  operatingModelStatuses: 'Operating model',
  partners: 'Outsourcing partner',
  assurance_level: 'Assurance',
  coverage: 'Coverage',
  applicability: 'Applicability',
  reportInclusion: 'Report inclusion',
  cis: 'CIS-18 reference',
};

/**
 * The name a CIA category goes by — on its root row, its chip and the +N
 * row. With one criteria it is that criteria's configured name,
 * since the category then opens straight onto that criteria's values and
 * nothing else would name it; with several it is the category's default
 * name, since its panel lists the criteria under their own names. Both
 * are configuration, read at render: a settings save that adds a second
 * criteria renames the row on the next render.
 */
export const ciaCategoryTitle = (
  category: CiaFilterCategory,
  criteria: CategoryCriteria[],
): string =>
  criteria.length === 1 ? criteria[0].name : CATEGORY_LABELS[category];
